import { useMemo, useRef, useState } from 'react';
import { Button, Card, Empty, Space, Spin, Table, Tag, Timeline, Typography, message } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { IconAlertOctagon, IconFile, IconFileText, IconPaperclip, IconUpload } from '@tabler/icons-react';
import type { ColumnsType } from 'antd/es/table';

import {
  useMedicalExamDetail,
  useMedicalExamEvents,
  useMedicalExamComments,
  useMedicalExamDocuments,
} from '@/entities/medical/api/medicalChecksApi';
import {
  checkTypeLabels,
  checkStatusLabels,
  admissionStatusLabels,
  decisionLabels,
  eventTypeLabels,
  docTypeLabels,
  type CheckType,
  type CheckStatus,
  type AdmissionStatus,
  type Decision,
  type MedicalExamEvent,
  type MedicalExamDocument,
} from '@/entities/medical/model/medicalChecksSchema';
import { medicalCheckStatusTagColor, medicalAdmissionTagColor } from '@/entities/medical/model/medicalCheckTableTags';
import { useAuthSession } from '@/entities/auth/api/hooks';
import { RecordCommentsSection, type RecordCommentItem } from '@/shared/ui/RecordCommentsSection';
import s from './MedicalRecordDetail.module.scss';

const { Text } = Typography;

const CRITICAL_STATUSES = new Set(['overdue', 'not_passed', 'failed']);

const DOC_ICON: Record<string, typeof IconFile> = {
  conclusion: IconFileText,
  referral: IconFileText,
  attachment: IconFile,
};

type JournalFilter = 'all' | 'events' | 'documents' | 'changes';

const JOURNAL_CHIPS: { key: JournalFilter; label: string }[] = [
  { key: 'all', label: 'Все' },
  { key: 'events', label: 'События' },
  { key: 'documents', label: 'Документы' },
  { key: 'changes', label: 'Изменения' },
];

const FILTER_EVENT_TYPES: Record<JournalFilter, Set<string> | null> = {
  all: null,
  events: new Set(['status_changed', 'decision_changed']),
  documents: new Set(['document_uploaded']),
  changes: new Set(['status_changed', 'decision_changed']),
};

function EventItem({ event }: { event: MedicalExamEvent }) {
  const statusLabel = event.new_value?.exam_status
    ? (checkStatusLabels[event.new_value.exam_status as CheckStatus] ?? String(event.new_value.exam_status))
    : null;

  const decisionLabel = event.new_value?.decision
    ? (decisionLabels[event.new_value.decision as Decision] ?? String(event.new_value.decision))
    : null;

  return (
    <>
      <Text type="secondary" className={s['root__activity-time']}>
        {new Date(event.changed_at).toLocaleString('ru-RU')}
      </Text>
      <div className={s['root__activity-event']}>
        {eventTypeLabels[event.event_type] ?? event.event_type}
        {statusLabel ? ` → ${statusLabel}` : null}
        {decisionLabel ? ` → ${decisionLabel}` : null}
      </div>
      {event.reason ? (
        <Text type="secondary" className={s['root__activity-author']}>
          {event.reason}
        </Text>
      ) : null}
    </>
  );
}

const documentColumns: ColumnsType<MedicalExamDocument> = [
  {
    title: 'Тип',
    dataIndex: 'doc_type',
    key: 'type',
    width: 140,
    render: (v: string) => <Tag>{docTypeLabels[v] ?? v}</Tag>,
  },
  {
    title: 'Файл',
    dataIndex: 'file_name',
    key: 'name',
    render: (name: string, row) => {
      const Icon = DOC_ICON[row.doc_type] ?? IconFile;
      return (
        <span className={s['root__activity-file']}>
          <Icon size={14} />
          {name}
        </span>
      );
    },
  },
  {
    title: 'Версия',
    dataIndex: 'version',
    key: 'ver',
    width: 80,
    render: (v: number) => `v${v}`,
  },
  {
    title: 'Дата загрузки',
    dataIndex: 'uploaded_at',
    key: 'date',
    width: 160,
    render: (v: string) => new Date(v).toLocaleString('ru-RU'),
  },
];

export function MedicalRecordDetail() {
  const { recordId } = useParams<{ recordId: string }>();
  const { data, isLoading } = useMedicalExamDetail(recordId ?? null);
  const { data: events } = useMedicalExamEvents(recordId ?? null);
  const { data: comments } = useMedicalExamComments(recordId ?? null);
  const { data: documents } = useMedicalExamDocuments(recordId ?? null);
  const { data: auth } = useAuthSession();
  const [journalFilter, setJournalFilter] = useState<JournalFilter>('all');
  const documentsFileInputRef = useRef<HTMLInputElement>(null);
  const commentsFileInputRef = useRef<HTMLInputElement>(null);

  const filteredEvents = useMemo(() => {
    if (!events) return [];
    const allowed = FILTER_EVENT_TYPES[journalFilter];
    if (!allowed) return events;
    return events.filter((e) => allowed.has(e.event_type));
  }, [events, journalFilter]);

  const commentItems: RecordCommentItem[] = useMemo(
    () =>
      (comments ?? []).map((c) => ({
        id: c.id,
        authorName: c.author_name?.trim() || 'Пользователь',
        body: c.body,
        createdAt: c.created_at,
        edited: c.is_edited,
      })),
    [comments],
  );

  const triggerDemoFilePick = (which: 'documents' | 'comments') => {
    message.info('В демо режиме файлы на сервер не загружаются. Выберите файл, чтобы проверить выбор.');
    (which === 'documents' ? documentsFileInputRef : commentsFileInputRef).current?.click();
  };

  const onDemoFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) message.success(`Выбран файл: ${f.name} (не сохранён в демо)`);
    e.target.value = '';
  };

  if (!recordId) {
    return <Empty description="Некорректный идентификатор записи" />;
  }

  const isCritical = data ? CRITICAL_STATUSES.has(data.status) : false;

  return (
    <Spin spinning={isLoading}>
      {!isLoading && !data ? (
        <Empty description="Запись не найдена" />
      ) : data ? (
        <div className={s.root}>
          <div className={s.root__breadcrumbs}>
            <Link to="/medical?tab=employees" className={s.root__crumbLink}>
              ← К списку медосмотров
            </Link>
            <span className={s.root__crumbSep}>/</span>
            <Link to={`/medical/employee/${data.employee_id}`} className={s.root__crumbLink}>
              {data.employee_full_name}
            </Link>
            <span className={s.root__crumbSep}>/</span>
            <span className={s.root__crumbCurrent}>
              {checkTypeLabels[data.check_type as CheckType] ?? data.check_type}
            </span>
          </div>

          <div className={s.root__hero}>
            <div className={s.root__heroInfo}>
              <div className={s.root__heroLabel}>Медосмотр</div>
              <div className={s.root__heroName}>
                {checkTypeLabels[data.check_type as CheckType] ?? data.check_type} — {data.employee_full_name}
              </div>
              <div className={s.root__heroMeta}>
                <Link
                  to={`/employees?employeeId=${data.employee_id}&tab=medical`}
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  {data.employee_full_name}
                </Link>{' '}
                · {data.position?.name ?? '—'} · {data.org_unit.name}
              </div>
              <div className={s.root__heroBadges}>
                <Tag color={medicalCheckStatusTagColor[data.status] ?? 'default'} className={s.root__statusTag}>
                  {checkStatusLabels[data.status as CheckStatus] ?? data.status}
                </Tag>
                {data.admission_status ? (
                  <Tag
                    color={medicalAdmissionTagColor[data.admission_status] ?? 'default'}
                    className={s.root__statusTag}
                  >
                    {admissionStatusLabels[data.admission_status as AdmissionStatus] ?? data.admission_status}
                  </Tag>
                ) : null}
              </div>
            </div>
          </div>

          {isCritical ? (
            <div className={s.root__problemAlert}>
              <IconAlertOctagon size={20} className={s.root__problemIcon} />
              <div className={s.root__problemContent}>
                <div className={s.root__problemTitle}>
                  {data.status === 'overdue'
                    ? 'Медосмотр просрочен'
                    : data.status === 'failed' || data.status === 'not_passed'
                      ? 'Медосмотр не пройден'
                      : 'Требуется внимание'}
                </div>
                <div className={s.root__problemDesc}>
                  {data.status === 'overdue'
                    ? 'Срок прохождения медосмотра истёк. Необходимо переназначить или зафиксировать результат.'
                    : 'Медосмотр не пройден. Свяжитесь с ответственным специалистом.'}
                </div>
              </div>
            </div>
          ) : null}

          <Card size="small" className={s.root__section}>
            <div className={s.root__sectionTitle}>Сведения о медосмотре</div>
            <div className={s.root__detailsGrid}>
              <div className={s.root__detailItem}>
                <div className={s.root__detailLabel}>Тип медосмотра</div>
                <div className={s.root__detailValue}>
                  {checkTypeLabels[data.check_type as CheckType] ?? data.check_type}
                </div>
              </div>
              <div className={s.root__detailItem}>
                <div className={s.root__detailLabel}>Статус</div>
                <div className={s.root__detailValue}>
                  <Tag color={medicalCheckStatusTagColor[data.status] ?? 'default'}>
                    {checkStatusLabels[data.status as CheckStatus] ?? data.status}
                  </Tag>
                </div>
              </div>
              <div className={s.root__detailItem}>
                <div className={s.root__detailLabel}>Дата прохождения</div>
                <div className={s.root__detailValue}>{data.checked_at ?? '—'}</div>
              </div>
              <div className={s.root__detailItem}>
                <div className={s.root__detailLabel}>Следующий медосмотр</div>
                <div className={s.root__detailValue}>{data.next_check_date ?? '—'}</div>
              </div>
              <div className={s.root__detailItem}>
                <div className={s.root__detailLabel}>Допуск к работе</div>
                <div className={s.root__detailValue}>
                  <Tag color={medicalAdmissionTagColor[data.admission_status] ?? 'default'}>
                    {admissionStatusLabels[data.admission_status as AdmissionStatus] ?? data.admission_status}
                  </Tag>
                </div>
              </div>
              {data.conclusion_summary ? (
                <div className={s.root__detailItem}>
                  <div className={s.root__detailLabel}>Заключение</div>
                  <div className={s.root__detailValue}>{data.conclusion_summary}</div>
                </div>
              ) : null}
              {data.restrictions ? (
                <div className={s.root__detailItem}>
                  <div className={s.root__detailLabel}>Ограничения</div>
                  <div className={s.root__detailValue}>{data.restrictions}</div>
                </div>
              ) : null}
            </div>
          </Card>

          <Card size="small" className={s.root__section}>
            <div className={s.root__sectionHeader}>
              <div className={s.root__sectionTitle}>Журнал медосмотра</div>
              <Space size={4}>
                {JOURNAL_CHIPS.map((chip) => (
                  <Tag
                    key={chip.key}
                    className={`${s.root__journalChip} ${journalFilter === chip.key ? s.root__journalChip_active : ''}`}
                    onClick={() => setJournalFilter(chip.key)}
                  >
                    {chip.label}
                  </Tag>
                ))}
              </Space>
            </div>
            {filteredEvents.length > 0 ? (
              <Timeline
                className={s.root__timeline}
                items={filteredEvents.map((e) => ({
                  key: e.id,
                  color:
                    e.new_value?.exam_status === 'passed'
                      ? 'green'
                      : CRITICAL_STATUSES.has(String(e.new_value?.exam_status ?? ''))
                        ? 'red'
                        : 'blue',
                  children: <EventItem event={e} />,
                }))}
              />
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {events && events.length > 0 ? 'Нет событий по выбранному фильтру' : 'Событий пока нет'}
              </Text>
            )}
          </Card>

          <Card size="small" className={s.root__section}>
            <input
              ref={documentsFileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              hidden
              onChange={onDemoFileChosen}
            />
            <div className={s.root__sectionHeader}>
              <div className={s.root__sectionTitle}>Документы</div>
              <Button size="small" icon={<IconUpload size={14} />} onClick={() => triggerDemoFilePick('documents')}>
                Прикрепить
              </Button>
            </div>
            {documents && documents.length > 0 ? (
              <Table dataSource={documents} columns={documentColumns} rowKey="id" size="small" pagination={false} />
            ) : (
              <div className={s.root__emptyBlock}>
                <IconPaperclip size={20} style={{ color: 'var(--n400)' }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Документы появятся по ходу медосмотра
                </Text>
              </div>
            )}
          </Card>

          <Card size="small" className={s.root__section}>
            <input
              ref={commentsFileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              hidden
              onChange={onDemoFileChosen}
            />
            <RecordCommentsSection
              items={commentItems}
              composerDisplayName={auth?.user.display_name}
              onAttach={() => triggerDemoFilePick('comments')}
              onMention={() => message.info('Упоминания будут доступны после подключения к серверу.')}
            />
          </Card>
        </div>
      ) : null}
    </Spin>
  );
}
